import React from 'react';

const URGENCY_COLORS = {
  red: { dot: 'bg-red-500', border: 'border-red-900/40', bg: 'hover:bg-red-950/20' },
  yellow: { dot: 'bg-yellow-500', border: 'border-yellow-900/30', bg: 'hover:bg-yellow-950/20' },
  green: { dot: 'bg-green-500', border: 'border-green-900/20', bg: 'hover:bg-gray-800/60' },
};

function Avatar({ name, urgency }) {
  const initials = name ? name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() : '?';
  const colors = { red: 'bg-red-800 text-red-200', yellow: 'bg-yellow-800 text-yellow-200', green: 'bg-gray-700 text-gray-200' };
  return (
    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${colors[urgency] || colors.green}`}>
      {initials}
    </div>
  );
}

function stripMarkdown(text) {
  if (!text) return null;
  return text
    .replace(/#{1,6} /g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/^- /gm, '')
    .replace(/\n+/g, ' ')
    .trim()
    .substring(0, 100);
}

const URGENCY_STATE_STYLES = {
  overdue:       { background: 'rgba(220,38,38,0.12)',  borderLeftColor: '#dc2626' },
  unread:        { background: 'rgba(59,130,246,0.10)', borderLeftColor: '#3b82f6' },
  due_today:     { background: 'rgba(245,158,11,0.10)', borderLeftColor: '#f59e0b' },
  high_priority: { background: 'rgba(245,158,11,0.07)', borderLeftColor: '#c9a84c' },
  attention:     { background: 'rgba(201,168,76,0.07)', borderLeftColor: '#c9a84c' },
  normal:        { background: 'transparent',            borderLeftColor: 'transparent' },
};

export default function CaseCard({ record, onClick, badge }) {
  const c = URGENCY_COLORS[record.urgency] || URGENCY_COLORS.green;
  const hasUnreadComms = badge?.unread_count > 0;
  const hasTaskAlert = !!badge?.task_alert;

  // Derive urgency state — badge signals can upgrade a 'normal' backend state
  const backendState = record.urgency_state || 'normal';
  const urgencyState = backendState !== 'normal' ? backendState :
    (hasTaskAlert && hasUnreadComms ? 'overdue' :
     hasUnreadComms ? 'unread' :
     hasTaskAlert ? 'due_today' : 'normal');
  const stateStyle = URGENCY_STATE_STYLES[urgencyState] || URGENCY_STATE_STYLES.normal;

  return (
    <button onClick={onClick}
      style={{ background: stateStyle.background, borderLeftColor: stateStyle.borderLeftColor, borderLeftWidth: '3px', borderLeftStyle: 'solid' }}
      className={`w-full text-left px-4 py-3 border-b border-gray-800 transition-colors flex gap-3 items-start relative`}>
      {(hasUnreadComms || hasTaskAlert) && (
        <div className="absolute top-1.5 right-1.5 flex flex-col items-end gap-1">
          {hasUnreadComms && (
            <div className="flex items-center gap-1"
                 title={`${badge.unread_count} unread ${(badge.channels || []).join(', ') || 'message'}`}>
              <div className="flex gap-0.5">
                {(badge.channels || []).includes('email') && (
                  <span className="text-sm">✉️</span>
                )}
                {(badge.channels || []).includes('sms') && (
                  <span className="text-sm">💬</span>
                )}
              </div>
              <div className="relative">
                <span className="animate-pulse absolute inset-0 rounded-full bg-red-500 opacity-40" />
                <span className="relative flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white shadow-lg">
                  {badge.unread_count > 9 ? '9+' : badge.unread_count}
                </span>
              </div>
            </div>
          )}
          {hasTaskAlert && (
            <span
              title="Task needs attention"
              className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[11px] font-bold text-white shadow-lg"
            >🔥</span>
          )}
        </div>
      )}
      <div className="relative">
        <Avatar name={record.title} urgency={record.urgency} />
        <span className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-gray-900 ${c.dot}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-100 text-sm font-medium truncate">{record.title}</span>
          <span className="text-gray-500 text-[10px] flex-shrink-0">{record.record_type_name}</span>
        </div>
        {record.critical_note && (
          <p className={`text-xs mt-0.5 truncate ${record.urgency === 'red' ? 'text-red-400' : record.urgency === 'yellow' ? 'text-yellow-400' : 'text-gray-400'}`}>
            {record.urgency === 'red' ? '⚠️ ' : record.urgency === 'yellow' ? '⏰ ' : '• '}{record.critical_note}
          </p>
        )}
        {record.last_ai_preview && (
          <p className="text-gray-500 text-[11px] mt-0.5 truncate italic">"{stripMarkdown(record.last_ai_preview)}"</p>
        )}
      </div>
    </button>
  );
}
